import React from 'react';
import { Button } from '@oysterjs/ui/button';

import type { CaraWorkspaceFieldsFragment } from '~/api/graphql';
import { buildArtifactStaticsUrl } from '~/components/workspaces/lib/artifact';
import { useArtifactVersionNumber } from '~/components/workspaces/hooks/use-artifact-version-number';
import { OfficeOnlineFilePreview } from '~/components/office-online-embed-url';
import { ArtifactFocusShell } from './artifact-focus-shell';
import { useArtifactEditor } from '~/components/workspaces/hooks/use-artifact-editor';
import { VisibilityPopover } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';

export const ApplicationFileFocus = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  artifact: CaraWorkspaceFieldsFragment['artifacts'][0];
  onClose: () => void;
  variant: 'xlsx' | 'docx';
}) => {
  const artifactVersionNumber = useArtifactVersionNumber();
  const { handleVisibilityUpdate } = useArtifactEditor({
    workspaceId: props.workspace.id,
    artifactId: props.artifact.id,
    version: props.artifact.version
  });
  const visibilityEditAllowed = useVisibilityEditAllowed(props.workspace.agencyUser?.id);

  const downloadUrl = React.useMemo(() => {
    if (!props.artifact?.id || !props.workspace.id) return '';
    return buildArtifactStaticsUrl(
      'application-file',
      props.workspace.id,
      props.artifact.id,
      props.artifact.version
    );
  }, [props.artifact?.id, props.artifact?.version, props.workspace.id]);

  const title =
    props.artifact?.applicationFile?.title ||
    (props.variant === 'docx' ? 'Word document' : 'Spreadsheet');
  const iframeTitle = props.variant === 'docx' ? 'Word document preview' : 'Excel workbook preview';

  return (
    <ArtifactFocusShell
      title={title}
      version={artifactVersionNumber(props.artifact)}
      onClose={props.onClose}
      headerActions={
        <div className="flex items-center gap-2 flex-none">
          {downloadUrl && (
            <Button href={downloadUrl} target="_blank" download color="white">
              Download
            </Button>
          )}
          {visibilityEditAllowed && (
            <VisibilityPopover
              visibility={props.artifact.visibility}
              onSave={handleVisibilityUpdate}
            />
          )}
        </div>
      }
    >
      <div className="relative mt-2 flex flex-1 flex-col overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700">
        <OfficeOnlineFilePreview downloadUrl={downloadUrl} iframeTitle={iframeTitle} />
      </div>
    </ArtifactFocusShell>
  );
};
